import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Sparkles } from "lucide-react";
import { supportContextCardTitle, supportInsetPanelAuto, supportSubpanelHeader } from "./supportStyles";
import { formatUtc } from "./sentryActivityUtils";
import { supportPatientPath } from "./supportUrls";

const VERDICTS = {
  recorded: { label: "Recorded on this patient", className: "text-green-800 bg-green-100" },
  recorded_on_other_patient: { label: "Recorded on another patient", className: "text-amber-800 bg-amber-100" },
  not_recorded: { label: "No recording started", className: "text-red-800 bg-red-100" },
  unclear: { label: "Unclear", className: "text-gray-700 bg-gray-100" },
};

const OUTCOMES = {
  uploaded: "Upload succeeded",
  upload_not_observed: "Upload not observed in this window",
  failed: "Recording failed",
};

const DiagnosisCard = ({ result: response, isStale }) => {
  const result = response?.result;
  if (!result) return null;

  const verdict = VERDICTS[result.verdict] || { label: result.verdict, className: "text-gray-700 bg-gray-100" };
  const evidence = result.evidence || [];
  const others = result.other_patient_ids || [];

  return (
    <div className={`${supportInsetPanelAuto} overflow-hidden`}>
      <div className={`${supportSubpanelHeader} flex items-center justify-between gap-2`}>
        <h4 className={supportContextCardTitle}>
          <Sparkles className="inline h-3.5 w-3.5 mr-1 text-ktx-purple -translate-y-px" aria-hidden />
          Did the provider hit record?
        </h4>
        <span className={`text-[11px] font-medium rounded-full px-2 py-0.5 ${verdict.className}`}>{verdict.label}</span>
      </div>

      <div className="px-3 py-3 space-y-3 text-sm text-gray-800">
        {response.problems?.length > 0 && (
          <div className="flex gap-2 text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
            <div>
              <p className="font-medium">Check this against the logs before acting on it.</p>
              <ul className="list-disc pl-4 text-xs mt-1">
                {response.problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {isStale && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            The logs changed since this ran. Re-run it to include them.
          </p>
        )}

        <p>{result.summary}</p>
        {result.recording_outcome && <p className="text-gray-700">{OUTCOMES[result.recording_outcome]}.</p>}
        {others.length > 0 && (
          <p className="text-gray-700">
            Recordings on other patients:{" "}
            {others.map((id, index) => (
              <React.Fragment key={id}>
                {index > 0 && ", "}
                <Link to={supportPatientPath(id)} className="text-ktx-purple hover:underline">
                  {id}
                </Link>
              </React.Fragment>
            ))}
          </p>
        )}

        {evidence.length > 0 && (
          <ul className="space-y-1">
            {evidence.map((item, index) => (
              <li key={index} className="flex gap-2">
                <span className="shrink-0 text-[11px] font-medium text-gray-600 bg-gray-100 rounded px-1.5 py-0.5 h-fit">
                  {item.type === "replay" ? `Replay ${String(item.id || "").slice(0, 8)}` : "Log"}
                </span>
                <span>
                  {item.time && <span className="text-xs text-gray-500 font-mono mr-1.5">{formatUtc(item.time)}</span>}
                  {item.note}
                </span>
              </li>
            ))}
          </ul>
        )}

        <p className="text-xs text-gray-500">
          Generated {formatUtc(response.generated_at)} · window {formatUtc(response.window?.start)} –{" "}
          {formatUtc(response.window?.end)}
        </p>
      </div>
    </div>
  );
};

export default DiagnosisCard;
